<template>
  <v-tooltip top>
    <template #activator="{ on }">
      <v-chip
        data-test="select-all-toggle"
        small
        :color="chipColor"
        v-on="on"
        @click.stop="() => emit('toggle', isAllSelected)"
        >{{ selectedCounter }}
      </v-chip>
    </template>

    {{ tooltipText }}
  </v-tooltip>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'

const props = defineProps<{
  totalItemCount?: number
  selectedItemCount?: number
  skipEmptyFilter?: boolean
  emptyIsAll?: boolean
}>()

const emit = defineEmits<{
  (event: 'toggle', isAllSelected: boolean)
}>()

const isAllSelected = ref(true)
const selectedCounter = ref('')

// old watch effect. Will be replaced with new watch effect below,
// once "emptyIsAll" becomes the default mode.
watchEffect(() => {
  if (props.emptyIsAll) return

  isAllSelected.value = props.totalItemCount === props.selectedItemCount

  if (isAllSelected.value) {
    selectedCounter.value = 'All'
  } else if (props.selectedItemCount === 0 && props.totalItemCount !== undefined) {
    selectedCounter.value = props.skipEmptyFilter ? 'N/A' : 'None'
  } else if (props.selectedItemCount === 0 && props.totalItemCount === undefined) {
    selectedCounter.value = 'All'
  } else if (props.selectedItemCount !== undefined && props.totalItemCount !== undefined) {
    selectedCounter.value = `${props.selectedItemCount} / ${props.totalItemCount}`
  } else if (props.selectedItemCount !== undefined && props.totalItemCount === undefined) {
    selectedCounter.value = `${props.selectedItemCount}`
  }
})

// new watch effect. keep this one once "emptyIsAll" is the default mode!
watchEffect(() => {
  if (!props.emptyIsAll) return

  isAllSelected.value = props.totalItemCount === props.selectedItemCount

  if (props.selectedItemCount && props.totalItemCount) {
    selectedCounter.value = `${props.selectedItemCount} / ${props.totalItemCount}`
  } else if (props.selectedItemCount) {
    selectedCounter.value = `${props.selectedItemCount}`
  } else {
    selectedCounter.value = 'All'
  }
})

const tooltipText = computed(() => {
  if (props.emptyIsAll) {
    return isAllSelected.value ? 'Click to reset' : 'Click to select all'
  }
  return isAllSelected.value ? 'Click to hide all' : 'Click to show all'
})

const chipColor = computed(() => {
  if (selectedCounter.value === 'All') {
    return 'var(--color-silver)'
  } else if (selectedCounter.value === 'None') {
    return 'var(--color-red--lighter)'
  } else if (selectedCounter.value === 'N/A') {
    return 'var(--color-yellow--lighter)'
  } else {
    return 'var(--color-blue--lighter)'
  }
})
</script>
